import React from "react";

export type TabType =
  | "chat"
  | "notes"
  | "tasks"
  | "custom"
  | "tracker"            // Project tracker
  | "timesheet"          // Weekly timesheet (coming soon)
  | "owner-requirements" // Owner Requirements tool
  | "calcera-ball";      // Calcera Ball easter egg

export type PaneId =
  | "center"
  | "fullscreen"
  | "left"
  | "right"
  | "top"
  | "bottom"
  | "topLeft"
  | "topRight"
  | "bottomLeft"
  | "bottomRight";

export type DropZone =
  | "center"
  | "fullscreen"
  | "tab-bar"
  | "left-half"
  | "right-half"
  | "top-half"
  | "bottom-half"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right";

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface WorkspaceTab {
  id: string;
  type: TabType;
  title: string;

  calculatorId?: string | null;
  calculatorName?: string | null;

  dirty?: boolean;
  /**
   * Optional project scoping for notes/tasks tabs.
   */
  projectId?: string | null;
  projectName?: string | null;
  clientName?: string | null;
  isMiscProject?: boolean;
  /**
   * Current tile for this tab's WindowFrame.
   * "fullscreen" represents occupying the entire workspace.
   */
  tilePosition: Exclude<DropZone, "tab-bar">;
  /**
   * Last time this tab was made active or otherwise interacted with,
   * used for least-recently-used heuristics when freeing space.
   */
  lastActiveAt?: number;
  /**
   * Whether this tab is minimized to the bottom bar.
   * We still keep it in the tabs array to preserve ordering and metadata.
   */
  isMinimized?: boolean;
    /**
   * Prevents dragging or snapping this window.
   * Used to lock a frame to its current quadrant.
   */
  isLocked?: boolean;

  /**
   * Tile position saved just before this tab was minimized,
   * so it can be restored to exactly where it was.
   */
  preMinimizeTile?: Exclude<DropZone, "tab-bar">;
}

export interface WorkspaceState {
  /**
   * All tabs, each potentially rendered as its own WindowFrame.
   */
  tabs: WorkspaceTab[];
  /**
   * Simple counters per type for generating readable default titles.
   */
  counters: Record<TabType, number>;
  /**
   * Cached tile rectangles computed from current workspace bounds.
   * Keys are DropZone values except "tab-bar".
   */
  tileRects: Partial<Record<Exclude<DropZone, "tab-bar">, Rect>>;
  /**
   * The tab currently focused by user interaction (for visual emphasis).
   */
  activeTabId?: string;
  /**
   * Window-level drag state for snapping/preview.
   */
  windowDragState?: WindowDragState | null;
}

export interface WindowDragState {
  isDragging: boolean;
  tabId: string;
  startPointer: { x: number; y: number };
  pointer: { x: number; y: number };
  dropZone: DropZone | null;
  previewRect: Rect | null;
}

export interface WorkspaceContextValue {
  state: WorkspaceState;
  /**
   * Basic tab ops
   */
  openTab: (
    type: TabType,
    options?: {
      forceNew?: boolean;
      projectId?: string | null;
      projectName?: string | null;
      clientName?: string | null;
      isMisc?: boolean;
      calculatorId?: string | null;
      calculatorName?: string | null;
    },
  ) => void;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  setTabTitle: (tabId: string, title: string) => void;
  markTabDirty: (tabId: string, dirty: boolean) => void;
  setTabScope: (
    tabId: string,
    scope: {
      projectId?: string | null;
      projectName?: string | null;
      clientName?: string | null;
      isMiscProject?: boolean;
    },
  ) => void;

  /**
   * Minimize/restore
   */
  minimizedTabIds: Set<string>;
  isTabMinimized: (tabId: string) => boolean;
  setTabMinimized: (tabId: string, minimized: boolean) => void;

  /**
   * Drag/snap for WindowFrames (by tab)
   */
  beginWindowDrag: (tabId: string, point: { x: number; y: number }) => void;
  updateWindowDrag: (point: { x: number; y: number }) => void;
  endWindowDrag: (opts?: { cancel?: boolean }) => void;
  applyTabSnap: (tabId: string, zone: Exclude<DropZone, "tab-bar">) => void;

  /**
   * Toggle maximize for a tab (fullscreen <-> previous tile)
   */
  toggleMaximize: (tabId: string) => void;

  toggleTabLocked: (tabId: string) => void;
  isTabLocked: (tabId: string) => boolean;

}

export const WorkspaceContext =
  React.createContext<WorkspaceContextValue | null>(null);

export function useWorkspace(): WorkspaceContextValue {
  const ctx = React.useContext(WorkspaceContext);
  if (!ctx) {
    throw new Error("useWorkspace must be used within a WorkspaceContext provider.");
  }
  return ctx;
}
